import { getValue, RemoteConfig } from "firebase/remote-config";

export type OnboardingStepId =
  | "welcome"
  | "role"
  | "preferences"
  | "experience"
  | "completion";

export type OnboardingRole =
  | "band_director"
  | "designer"
  | "instructor"
  | "student";

/** UI labels shown in role picker (must stay in sync with AccountSetupStep) */
export const ROLE_UI_OPTIONS = [
  "Band director",
  "Designer",
  "Instructor",
  "Student",
] as const;

export const UI_LABEL_TO_ROLE: Record<string, OnboardingRole> = {
  "Band director": "band_director",
  Director: "band_director",
  Designer: "designer",
  Instructor: "instructor",
  Student: "student",
};

export const FULL_FLOW_STEPS: OnboardingStepId[] = [
  "welcome",
  "role",
  "preferences",
  "experience",
  "completion",
];

export const FAST_FLOW_STEPS: OnboardingStepId[] = [
  "welcome",
  "role",
  "completion",
];

export function resolveFlowSteps(options: {
  fastPath: boolean;
}): { steps: OnboardingStepId[]; pathId: string } {
  if (options.fastPath) {
    return { steps: FAST_FLOW_STEPS, pathId: "fast_v1" };
  }
  return { steps: FULL_FLOW_STEPS, pathId: "full_v1" };
}

/** Role-specific default template (Firestore drillTemplates id); empty = blank drill */
export function getDefaultTemplateIdForRole(
  rc: RemoteConfig,
  role: OnboardingRole | null | undefined
): string {
  if (!role) return "";
  const key = `onboarding_template_${role}` as const;
  const v = getValue(rc, key).asString();
  return v && v.trim() ? v.trim() : "";
}

export function getEditorWelcomeForRole(
  role: OnboardingRole | null
): { title: string; body: string } | null {
  if (!role) return null;
  const m: Record<OnboardingRole, { title: string; body: string }> = {
    band_director: {
      title: "Welcome to DrillFlo",
      body: "As a director, you will start by building your roster and placing performers—then playback, sets, and music. This short tour keeps you moving toward a first usable drill fast.",
    },
    designer: {
      title: "Welcome to DrillFlo",
      body: "You will start by getting performers on the field, then cover playback, sets, and tools for precise design. Click Next when you are ready.",
    },
    instructor: {
      title: "Welcome to DrillFlo",
      body: "We will walk through performers, playback, and sets so you can teach and clean drill efficiently. Click Next to begin.",
    },
    student: {
      title: "Welcome to DrillFlo",
      body: "Follow the tour to see how drills are built: performers on the field, playback, and sets. Click Next to start.",
    },
  };
  return m[role] ?? null;
}

/** Matches onboarding copy: "No, I'm new to this" */
export function isBeginnerPriorExperience(
  priorExperience: string | null | undefined
): boolean {
  if (!priorExperience) return false;
  const v = priorExperience.trim().toLowerCase();
  return v.includes("new to this") || v === "no" || v.startsWith("no,");
}

/** Ensemble size buckets from onboarding: 100-200 / 200+ */
export function isLargeEnsembleSize(
  typicalPerformerCount: string | null | undefined
): boolean {
  if (!typicalPerformerCount) return false;
  const v = typicalPerformerCount.trim();
  return v === "100-200" || v === "200+" || v.startsWith("100") || v.startsWith("200");
}

export type EditorFirstRunPath = {
  isBeginner: boolean;
  isLargeEnsemble: boolean;
};

export function resolveEditorFirstRunPath(input: {
  priorExperience?: string | null;
  typicalPerformerCount?: string | null;
}): EditorFirstRunPath {
  return {
    isBeginner: isBeginnerPriorExperience(input.priorExperience),
    isLargeEnsemble: isLargeEnsembleSize(input.typicalPerformerCount),
  };
}
